'use client';

import { useState } from 'react';
import { Link } from '@/i18n/routing';
import dynamic from 'next/dynamic';
import { Bikepark } from '@/types';
import FavoriteButton from './FavoriteButton';
import ReviewsList from './ReviewsList';
import ReviewForm from './ReviewForm';
import TrailCard from './TrailCard';
import WeatherWidget from './WeatherWidget';
import { useEffect } from 'react';
import { trackBikeparkView } from '@/lib/analytics';
import { useTranslations } from 'next-intl';
import {
  MapPin,
  Mountain,
  Home,
  ChevronRight,
  ExternalLink,
  ArrowRight,
  Ticket,
  CableCar,
  Users,
  ParkingCircle,
  Utensils,
  Hotel,
  Wrench,
  GraduationCap,
  Lock,
  Wifi,
  Shield,
  Copy,
  Check,
  Bike,
  Navigation,
  Map
} from 'lucide-react';

const SingleLocationMap = dynamic(() => import('./SingleLocationMap'), {
  ssr: false,
  loading: () => <div className="map-loading">Ładowanie mapy...</div>,
});

interface BikeparkDetailProps {
  bikepark: Bikepark;
  locale: string;
}

export default function BikeparkDetail({ bikepark, locale }: BikeparkDetailProps) {
  const t = useTranslations('bikeparkDetail');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    trackBikeparkView(bikepark.slug, bikepark.id, locale);
  }, [bikepark.slug, bikepark.id, locale]);

  const shareUrl = `https://bikeparkmapa.pl/${locale}/bikepark/${bikepark.slug}`;

  const difficultyColors: Record<string, string> = {
    green: '#22c55e',
    blue: '#3b82f6',
    red: '#ef4444',
    black: '#1f2937'
  };

  const difficultyLabels: Record<string, string> = {
    green: t('difficultyGreen'),
    blue: t('difficultyBlue'),
    red: t('difficultyRed'),
    black: t('difficultyBlack')
  };

  const difficultyDesc: Record<string, string> = {
    green: locale === 'pl' ? 'Łatwe' : 'Easy',
    blue: locale === 'pl' ? 'Średnie' : 'Medium',
    red: locale === 'pl' ? 'Trudne' : 'Hard',
    black: locale === 'pl' ? 'Eksperckie' : 'Expert'
  };

  const hasInfrastructure = bikepark.lift || bikepark.familyFriendly || bikepark.parking ||
    bikepark.restaurant || bikepark.accommodation || bikepark.bikeService ||
    bikepark.school || bikepark.locker || bikepark.wifi;

  return (
    <div className="bikepark-page">
      {/* Hero */}
      <section className="bikepark-hero">
        <div className="bikepark-hero-inner">
          {/* Breadcrumb */}
          <nav className="bikepark-breadcrumb">
            <Link href="/"><Home size={14} /> {t('home')}</Link>
            <ChevronRight size={14} />
            <Link href="/bikeparki">{t('bikeparks')}</Link>
            <ChevronRight size={14} />
            <span>{bikepark.name}</span>
          </nav>

          {/* Title */}
          <h1 className="bikepark-title">{bikepark.name}</h1>

          {/* Location */}
          {bikepark.region && (
            <p className="bikepark-location">
              <MapPin size={16} />
              {bikepark.region}
            </p>
          )}

          {/* Stats Bar */}
          <div className="bikepark-stats-bar">
            {/* Difficulty Levels */}
            {bikepark.levels && bikepark.levels.length > 0 && bikepark.levels.map((level: string) => (
              <div key={level} className="stat-item">
                <div className="stat-icon" style={{ background: difficultyColors[level] || '#6b7280' }}>
                  <Mountain size={20} color="#fff" />
                </div>
                <div className="stat-text">
                  <span className="stat-value" style={{ color: difficultyColors[level] }}>
                    {difficultyLabels[level] || level}
                  </span>
                  <span className="stat-label">{difficultyDesc[level]}</span>
                </div>
              </div>
            ))}

            {/* Trail Count */}
            {bikepark.trailsCount !== undefined && bikepark.trailsCount > 0 && (
              <div className="stat-item">
                <div className="stat-icon stat-icon-trails">
                  <Bike size={20} />
                </div>
                <div className="stat-text">
                  <span className="stat-value">{bikepark.trailsCount}</span>
                  <span className="stat-label">{locale === 'pl' ? 'Tras' : 'Trails'}</span>
                </div>
              </div>
            )}

            {/* Price */}
            {bikepark.price !== undefined && bikepark.price !== null && (
              <div className={`stat-item ${bikepark.price === 0 ? 'stat-free' : ''}`}>
                <div className={`stat-icon ${bikepark.price === 0 ? 'stat-icon-free' : ''}`}>
                  {bikepark.price === 0 ? <span>✓</span> : <Ticket size={20} />}
                </div>
                <div className="stat-text">
                  <span className="stat-value">
                    {bikepark.price === 0 ? t('freeEntry') : `${bikepark.price} zł`}
                  </span>
                  <span className="stat-label">{bikepark.price === 0 ? '' : (locale === 'pl' ? '/dzień' : '/day')}</span>
                </div>
              </div>
            )}

            {/* Favorite */}
            <div className="stat-item stat-favorite">
              <FavoriteButton
                id={bikepark.documentId}
                slug={bikepark.slug}
                name={bikepark.name}
                type="bikepark"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Content */}
      <div className="bikepark-content">
        <div className="bikepark-layout">
          {/* Main */}
          <main className="bikepark-main">
            {/* Location Map - First, Full Width */}
            {bikepark.lat && bikepark.lng && (
              <div className="bikepark-card location-card">
                <h2><MapPin size={20} /> {t('locationOnMap')}</h2>
                <div className="location-map-container">
                  <SingleLocationMap
                    lat={bikepark.lat}
                    lng={bikepark.lng}
                    name={bikepark.name}
                    zoom={13}
                  />
                </div>
                <div className="location-info">
                  <span className="location-coords">
                    <MapPin size={14} />
                    {bikepark.lat.toFixed(4)}, {bikepark.lng.toFixed(4)}
                  </span>
                  {bikepark.region && (
                    <span className="location-region">{bikepark.region}</span>
                  )}
                </div>
                <div className="location-actions">
                  <a
                    href={`https://www.google.com/maps?q=${bikepark.lat},${bikepark.lng}`}
                    target="_blank"
                    rel="nofollow noopener noreferrer"
                    className="location-btn"
                  >
                    <ExternalLink size={16} /> {locale === 'pl' ? 'Otwórz w mapach' : 'Open in Maps'}
                  </a>
                  <a
                    href={`https://www.google.com/maps/dir/?api=1&destination=${bikepark.lat},${bikepark.lng}`}
                    target="_blank"
                    rel="nofollow noopener noreferrer"
                    className="location-btn location-btn-primary"
                  >
                    <Navigation size={16} /> {locale === 'pl' ? 'Nawiguj' : 'Get Directions'}
                  </a>
                </div>
              </div>
            )}

            {/* Description */}
            {(bikepark.blurb || bikepark.description) && (
              <div className="bikepark-card">
                <h2>{t('about')}</h2>
                <p className="bikepark-description">{bikepark.blurb || bikepark.description}</p>
              </div>
            )}

            {/* Trails Section */}
            {((bikepark.trailsList && bikepark.trailsList.length > 0) || (bikepark.trails && bikepark.trails.length > 0)) && (
              <div className="bikepark-card">
                <h2>{t('trails')}{(bikepark.trailsCount || bikepark.trailsList?.length || bikepark.trails?.length) ? ` (${bikepark.trailsCount || bikepark.trailsList?.length || bikepark.trails?.length})` : ''}</h2>
                <div className="trails-list">
                  {(bikepark.trailsList && bikepark.trailsList.length > 0
                    ? bikepark.trailsList
                    : bikepark.trails || []
                  ).map((trail: any, idx: number) => (
                    <TrailCard key={trail.id || trail.slug || idx} trail={trail} bikeparkSlug={bikepark.slug} />
                  ))}
                </div>
              </div>
            )}

            {/* Styles */}
            {bikepark.styles && bikepark.styles.length > 0 && (
              <div className="bikepark-card">
                <h2>{t('trailTypes')}</h2>
                <div className="styles-grid">
                  {bikepark.styles.map((style: string) => (
                    <span key={style} className="style-tag">{style}</span>
                  ))}
                </div>
              </div>
            )}

            {/* Infrastructure */}
            {hasInfrastructure && (
              <div className="bikepark-card">
                <h2>{t('infrastructure')}</h2>
                <div className="infra-grid">
                  {bikepark.lift && (
                    <div className="infra-item">
                      <CableCar size={24} />
                      <span>{t('lift')}</span>
                    </div>
                  )}
                  {bikepark.familyFriendly && (
                    <div className="infra-item">
                      <Users size={24} />
                      <span>{t('familyFriendly')}</span>
                    </div>
                  )}
                  {bikepark.parking && (
                    <div className="infra-item">
                      <ParkingCircle size={24} />
                      <span>{t('parking')}</span>
                    </div>
                  )}
                  {bikepark.restaurant && (
                    <div className="infra-item">
                      <Utensils size={24} />
                      <span>{t('restaurant')}</span>
                    </div>
                  )}
                  {bikepark.accommodation && (
                    <div className="infra-item">
                      <Hotel size={24} />
                      <span>{t('accommodation')}</span>
                    </div>
                  )}
                  {bikepark.bikeService && (
                    <div className="infra-item">
                      <Wrench size={24} />
                      <span>{t('bikeService')}</span>
                    </div>
                  )}
                  {bikepark.school && (
                    <div className="infra-item">
                      <GraduationCap size={24} />
                      <span>{t('school')}</span>
                    </div>
                  )}
                  {bikepark.locker && (
                    <div className="infra-item">
                      <Lock size={24} />
                      <span>{t('lockers')}</span>
                    </div>
                  )}
                  {bikepark.wifi && (
                    <div className="infra-item">
                      <Wifi size={24} />
                      <span>{t('wifi')}</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Pricing Section */}
            {(bikepark.price !== undefined && bikepark.price !== null && bikepark.price > 0) && (
              <div className="bikepark-card pricing-card">
                <h2>{t('pricing')}</h2>
                <div className="price-box">
                  <div className="price-label">{t('dailyPass')}</div>
                  <div className="price-amount">{bikepark.price} zł</div>
                  {bikepark.priceInfo && (
                    <div className="price-info">{bikepark.priceInfo}</div>
                  )}
                </div>
                {bikepark.ticketUrl && (
                  <a
                    href={bikepark.ticketUrl}
                    target="_blank"
                    rel="nofollow noopener noreferrer"
                    className="price-btn"
                  >
                    <Ticket size={16} /> {t('buyTicket')}
                  </a>
                )}
              </div>
            )}

            {/* Reviews Section */}
            <ReviewsList parkId={bikepark.documentId} parkType="bikepark" />
            <ReviewForm parkId={bikepark.documentId} parkType="bikepark" parkName={bikepark.name} />
          </main>

          {/* Sidebar */}
          <aside className="bikepark-sidebar">
            {/* Weather Widget - standalone, no card wrapper */}
            {bikepark.lat && bikepark.lng && (
              <WeatherWidget
                lat={bikepark.lat}
                lng={bikepark.lng}
                locationName={bikepark.name}
                compact
              />
            )}

            {/* Quick Links */}
            <div className="sidebar-card">
              <h3>{locale === 'pl' ? 'Szybkie linki' : 'Quick Links'}</h3>

              <div className="sidebar-links">
                {bikepark.url && (
                  <a
                    href={bikepark.url}
                    target="_blank"
                    rel="nofollow noopener noreferrer"
                    className="sidebar-link sidebar-link-primary"
                  >
                    <ExternalLink size={16} /> {t('visitWebsite')}
                  </a>
                )}
                {bikepark.trailforksUrl && (
                  <a
                    href={bikepark.trailforksUrl}
                    target="_blank"
                    rel="nofollow noopener noreferrer"
                    className="sidebar-link"
                  >
                    <Map size={16} /> {t('viewOnTrailforks')}
                  </a>
                )}
              </div>

              {(bikepark.season || bikepark.openingHours) && (
                <div className="sidebar-meta">
                  {bikepark.season && (
                    <div className="meta-item">
                      <span className="meta-icon">📅</span>
                      <span>{bikepark.season}</span>
                    </div>
                  )}
                  {bikepark.openingHours && (
                    <div className="meta-item">
                      <span className="meta-icon">🕐</span>
                      <span>{bikepark.openingHours}</span>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Affiliate Gear Card */}
            <div className="sidebar-card gear-card">
              <h3><Shield size={16} /> {locale === 'pl' ? 'Polecane Kaski MTB' : 'Recommended MTB Helmets'}</h3>
              <p className="gear-intro">
                {locale === 'pl'
                  ? 'Sprawdzone kaski do bikeparku:'
                  : 'Tested helmets for the bikepark:'}
              </p>
              <div className="gear-list">
                <a
                  href="https://amzn.to/4rM7sjl"
                  target="_blank"
                  rel="noopener sponsored"
                  className="gear-item"
                >
                  <div className="gear-icon"><Shield size={20} /></div>
                  <div className="gear-info">
                    <span className="gear-name">MTB Enduro 2.0</span>
                    <span className="gear-desc">{locale === 'pl' ? 'Fullface - TOP wybór' : 'Fullface - TOP pick'}</span>
                  </div>
                  <ExternalLink size={14} className="gear-arrow" />
                </a>
                <a
                  href="https://amzn.to/4a68yAf"
                  target="_blank"
                  rel="noopener sponsored"
                  className="gear-item"
                >
                  <div className="gear-icon"><Shield size={20} /></div>
                  <div className="gear-info">
                    <span className="gear-name">Leatt Gravity 1.0</span>
                    <span className="gear-desc">{locale === 'pl' ? 'Lekki i wytrzymały' : 'Light & durable'}</span>
                  </div>
                  <ExternalLink size={14} className="gear-arrow" />
                </a>
                <a
                  href="https://amzn.to/3MkA4jH"
                  target="_blank"
                  rel="noopener sponsored"
                  className="gear-item"
                >
                  <div className="gear-icon"><Shield size={20} /></div>
                  <div className="gear-info">
                    <span className="gear-name">Leatt MTB 4.0</span>
                    <span className="gear-desc">{locale === 'pl' ? 'Świetna cena/jakość' : 'Great value'}</span>
                  </div>
                  <ExternalLink size={14} className="gear-arrow" />
                </a>
              </div>
              <p className="gear-disclaimer">
                {locale === 'pl' ? '* Linki partnerskie Amazon' : '* Amazon affiliate links'}
              </p>
            </div>

            {/* Social Share */}
            <div className="sidebar-card">
              <h3>{locale === 'pl' ? 'Udostępnij' : 'Share'}</h3>
              <div className="share-buttons">
                {/* Copy Link */}
                <button
                  onClick={async () => {
                    await navigator.clipboard.writeText(shareUrl);
                    setCopied(true);
                    setTimeout(() => setCopied(false), 2000);
                  }}
                  className="share-btn share-btn-copy"
                  title={locale === 'pl' ? 'Kopiuj link' : 'Copy link'}
                >
                  {copied ? <Check size={18} /> : <Copy size={18} />}
                  <span>{copied ? (locale === 'pl' ? 'Skopiowano!' : 'Copied!') : (locale === 'pl' ? 'Kopiuj link' : 'Copy link')}</span>
                </button>

                {/* Social Icons Row */}
                <div className="share-icons">
                  {/* Facebook */}
                  <a
                    href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="share-icon share-icon-facebook"
                    title="Facebook"
                  >
                    <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
                      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                    </svg>
                  </a>

                  {/* Twitter/X */}
                  <a
                    href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(bikepark.name)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="share-icon share-icon-x"
                    title="X (Twitter)"
                  >
                    <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
                      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                    </svg>
                  </a>

                  {/* WhatsApp */}
                  <a
                    href={`https://wa.me/?text=${encodeURIComponent(bikepark.name + ' ' + shareUrl)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="share-icon share-icon-whatsapp"
                    title="WhatsApp"
                  >
                    <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                    </svg>
                  </a>

                  {/* Email */}
                  <a
                    href={`mailto:?subject=${encodeURIComponent(bikepark.name)}&body=${encodeURIComponent((bikepark.blurb || '') + '\n\n' + shareUrl)}`}
                    className="share-icon share-icon-email"
                    title="Email"
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="20" height="20">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                  </a>
                </div>
              </div>
            </div>

            {/* Back to bikeparks */}
            <div className="sidebar-card back-card">
              <h3>{t('bikeparks')}</h3>
              <p className="back-text">{locale === 'pl' ? 'Wróć do mapy bikeparków' : 'Back to bikeparks map'}</p>
              <Link href="/bikeparki" className="back-link">
                {t('backToMap')} <ArrowRight size={16} />
              </Link>
            </div>
          </aside>
        </div>
      </div>

      <style jsx global>{`
        .bikepark-page {
          min-height: 100vh;
          background: var(--bg, #f8fafc);
        }

        /* Hero - green MTB gradient */
        .bikepark-hero {
          padding: 100px 20px 40px;
          background: linear-gradient(135deg, #16a34a 0%, #15803d 100%);
        }

        .bikepark-hero-inner {
          max-width: 1100px;
          margin: 0 auto;
        }

        .bikepark-breadcrumb {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-bottom: 20px;
          font-size: 14px;
          color: rgba(255, 255, 255, 0.8);
        }

        .bikepark-breadcrumb a {
          color: rgba(255, 255, 255, 0.8);
          text-decoration: none;
          display: flex;
          align-items: center;
          gap: 4px;
        }

        .bikepark-breadcrumb a:hover {
          color: #ffffff;
        }

        .bikepark-breadcrumb svg {
          color: rgba(255, 255, 255, 0.5);
        }

        .bikepark-breadcrumb > span:last-child {
          color: #ffffff;
          font-weight: 500;
        }

        .bikepark-title {
          font-size: clamp(28px, 5vw, 40px);
          font-weight: 800;
          color: #ffffff;
          margin: 0 0 12px;
          text-shadow: 0 2px 4px rgba(0,0,0,0.1);
        }

        .bikepark-location {
          display: flex;
          align-items: center;
          gap: 6px;
          color: rgba(255, 255, 255, 0.9);
          font-size: 16px;
          margin: 0 0 24px;
        }

        /* Stats Bar */
        .bikepark-stats-bar {
          display: flex;
          flex-wrap: wrap;
          gap: 16px;
          padding: 20px;
          background: var(--panel, #fff);
          border-radius: 16px;
          border: 1px solid var(--border, #e5e7eb);
          box-shadow: 0 2px 12px rgba(0,0,0,0.06);
        }

        .stat-item {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 8px 16px 8px 8px;
          background: var(--bg, #f8fafc);
          border-radius: 12px;
          min-width: 140px;
        }

        .stat-free {
          background: linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%);
        }

        .stat-icon {
          width: 40px;
          height: 40px;
          border-radius: 10px;
          background: #16a34a;
          color: #fff;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }

        .stat-icon-trails {
          background: #0ea5e9;
        }

        .stat-icon-free {
          background: #22c55e;
          font-size: 18px;
          font-weight: bold;
        }

        .stat-text {
          display: flex;
          flex-direction: column;
          gap: 2px;
        }

        .stat-value {
          font-size: 16px;
          font-weight: 700;
          color: var(--text, #1f2937);
        }

        .stat-label {
          font-size: 12px;
          color: var(--muted, #6b7280);
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .stat-favorite {
          padding: 8px;
          min-width: auto;
          margin-left: auto;
        }

        /* Content */
        .bikepark-content {
          max-width: 1100px;
          margin: 0 auto;
          padding: 32px 20px;
        }

        .bikepark-layout {
          display: grid;
          grid-template-columns: 1fr 320px;
          gap: 32px;
        }

        @media (max-width: 900px) {
          .bikepark-layout {
            grid-template-columns: 1fr;
          }
        }

        .bikepark-main {
          display: flex;
          flex-direction: column;
          gap: 24px;
        }

        .bikepark-card {
          background: var(--panel, #fff);
          border-radius: 16px;
          padding: 24px;
          border: 1px solid var(--border, #e5e7eb);
        }

        .bikepark-card h2 {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 18px;
          font-weight: 700;
          color: var(--text, #1f2937);
          margin: 0 0 16px;
          padding-bottom: 12px;
          border-bottom: 1px solid var(--border, #e5e7eb);
        }

        .bikepark-description {
          font-size: 15px;
          line-height: 1.7;
          color: var(--text-weak, #4b5563);
          margin: 0;
        }

        /* Location Card */
        .location-map-container {
          height: 300px;
          border-radius: 12px;
          overflow: hidden;
          margin-bottom: 12px;
        }

        .location-info {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 12px 16px;
          background: var(--bg, #f8fafc);
          border-radius: 10px;
          margin-bottom: 12px;
          font-size: 14px;
          color: var(--text-weak, #4b5563);
        }

        .location-coords {
          display: flex;
          align-items: center;
          gap: 6px;
          font-family: monospace;
          font-size: 13px;
        }

        .location-coords svg {
          color: #16a34a;
        }

        .location-region {
          font-weight: 500;
          color: var(--text, #1f2937);
        }

        .location-actions {
          display: flex;
          gap: 12px;
        }

        .location-btn {
          flex: 1;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          padding: 12px 16px;
          border-radius: 10px;
          font-size: 14px;
          font-weight: 600;
          text-decoration: none;
          transition: all 0.2s;
          background: var(--bg, #f8fafc);
          border: 1px solid var(--border, #e5e7eb);
          color: var(--text, #1f2937);
        }

        .location-btn:hover {
          border-color: #16a34a;
          color: #16a34a;
        }

        .location-btn-primary {
          background: linear-gradient(135deg, #16a34a 0%, #15803d 100%);
          border-color: transparent;
          color: #fff;
        }

        .location-btn-primary:hover {
          background: linear-gradient(135deg, #15803d 0%, #166534 100%);
          color: #fff;
        }

        /* Trails */
        .trails-list {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
          gap: 16px;
        }

        /* Infrastructure Grid */
        .infra-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
          gap: 12px;
        }

        .infra-item {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 8px;
          padding: 16px;
          background: var(--bg, #f8fafc);
          border-radius: 12px;
          border: 1px solid var(--border, #e5e7eb);
          color: var(--text, #1f2937);
          text-align: center;
          font-size: 13px;
          transition: all 0.2s;
        }

        .infra-item:hover {
          border-color: #16a34a;
          color: #16a34a;
        }

        .infra-item svg {
          color: #16a34a;
        }

        /* Styles */
        .styles-grid {
          display: flex;
          flex-wrap: wrap;
          gap: 10px;
        }

        .style-tag {
          padding: 8px 16px;
          background: linear-gradient(135deg, rgba(22, 163, 74, 0.1) 0%, rgba(21, 128, 61, 0.1) 100%);
          border: 1px solid rgba(22, 163, 74, 0.3);
          border-radius: 20px;
          color: var(--text, #1f2937);
          font-size: 13px;
          font-weight: 500;
        }

        /* Pricing */
        .pricing-card {
          background: linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%);
          border-color: #16a34a;
        }

        .pricing-card h2 {
          color: #166534;
          border-color: rgba(22, 101, 52, 0.2);
        }

        .price-box {
          text-align: center;
          padding: 20px;
          background: rgba(255,255,255,0.8);
          border-radius: 12px;
          margin-bottom: 16px;
        }

        .price-label {
          font-size: 14px;
          color: #166534;
          margin-bottom: 8px;
        }

        .price-amount {
          font-size: 32px;
          font-weight: 800;
          color: #15803d;
        }

        .price-info {
          font-size: 13px;
          color: #166534;
          margin-top: 8px;
        }

        .price-btn {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          padding: 14px 20px;
          background: linear-gradient(135deg, #16a34a 0%, #15803d 100%);
          border-radius: 10px;
          color: #fff;
          font-weight: 600;
          text-decoration: none;
          transition: all 0.2s;
        }

        .price-btn:hover {
          background: linear-gradient(135deg, #15803d 0%, #166534 100%);
        }

        /* Sidebar */
        .bikepark-sidebar {
          display: flex;
          flex-direction: column;
          gap: 20px;
        }

        .sidebar-card {
          background: var(--panel, #fff);
          border-radius: 16px;
          padding: 20px;
          border: 1px solid var(--border, #e5e7eb);
        }

        .sidebar-card h3 {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 15px;
          font-weight: 700;
          color: var(--text, #1f2937);
          margin: 0 0 16px;
          padding-bottom: 12px;
          border-bottom: 1px solid var(--border, #e5e7eb);
        }

        .detail-row {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 10px 0;
          border-bottom: 1px solid var(--border, #e5e7eb);
          font-size: 14px;
          color: var(--text-weak, #4b5563);
        }

        .detail-row:last-of-type {
          border-bottom: none;
        }

        .detail-row svg {
          color: #16a34a;
          flex-shrink: 0;
        }

        .detail-row span {
          flex: 1;
        }

        .detail-row strong {
          color: var(--text, #1f2937);
          font-weight: 600;
        }

        .sidebar-links {
          display: flex;
          flex-direction: column;
          gap: 10px;
          margin-top: 16px;
        }

        .sidebar-link {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 12px 16px;
          background: var(--bg, #f8fafc);
          border-radius: 10px;
          color: var(--text, #1f2937);
          text-decoration: none;
          font-size: 14px;
          font-weight: 500;
          transition: all 0.2s;
          border: 1px solid var(--border, #e5e7eb);
        }

        .sidebar-link:hover {
          background: #16a34a;
          color: #fff;
          border-color: #16a34a;
        }

        .sidebar-link-primary {
          background: #16a34a;
          color: #fff;
          border-color: #16a34a;
        }

        .sidebar-link-primary:hover {
          background: #15803d;
          border-color: #15803d;
        }

        .sidebar-meta {
          margin-top: 16px;
          padding-top: 16px;
          border-top: 1px solid var(--border, #e5e7eb);
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .meta-item {
          display: flex;
          align-items: center;
          gap: 10px;
          font-size: 14px;
          color: var(--text-weak, #4b5563);
        }

        .meta-icon {
          font-size: 16px;
        }

        /* Gear Affiliate Card */
        .gear-card {
          background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
          border-color: #f59e0b;
        }

        .gear-card h3 {
          color: #92400e;
          border-color: rgba(146, 64, 14, 0.2);
        }

        .gear-intro {
          font-size: 13px;
          color: #92400e;
          margin: 0 0 12px;
        }

        .gear-list {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .gear-item {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 10px 12px;
          background: rgba(255,255,255,0.8);
          border-radius: 10px;
          text-decoration: none;
          transition: all 0.2s;
          border: 1px solid rgba(245, 158, 11, 0.3);
        }

        .gear-item:hover {
          background: #fff;
          transform: translateX(4px);
          border-color: #f59e0b;
        }

        .gear-icon {
          width: 36px;
          height: 36px;
          border-radius: 8px;
          background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
          color: #fff;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }

        .gear-info {
          flex: 1;
          min-width: 0;
        }

        .gear-name {
          display: block;
          font-size: 13px;
          font-weight: 600;
          color: #1f2937;
        }

        .gear-desc {
          display: block;
          font-size: 11px;
          color: #6b7280;
        }

        .gear-arrow {
          color: #9ca3af;
          flex-shrink: 0;
        }

        .gear-item:hover .gear-arrow {
          color: #f59e0b;
        }

        .gear-disclaimer {
          font-size: 10px;
          color: #92400e;
          text-align: center;
          margin: 8px 0 0;
          opacity: 0.7;
        }

        /* Share Buttons */
        .share-buttons {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        .share-btn {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          padding: 12px 16px;
          border-radius: 10px;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s;
          border: 1px solid var(--border, #e5e7eb);
        }

        .share-btn-copy {
          background: var(--bg, #f8fafc);
          color: var(--text, #1f2937);
        }

        .share-btn-copy:hover {
          background: #16a34a;
          color: #fff;
          border-color: #16a34a;
        }

        .share-icons {
          display: flex;
          gap: 8px;
          justify-content: center;
        }

        .share-icon {
          width: 44px;
          height: 44px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s;
          color: #fff;
        }

        .share-icon:hover {
          transform: scale(1.1);
        }

        .share-icon-facebook {
          background: #1877F2;
        }

        .share-icon-x {
          background: #000;
        }

        .share-icon-whatsapp {
          background: #25D366;
        }

        .share-icon-email {
          background: #6b7280;
        }

        /* Back card */
        .back-card {
          background: linear-gradient(135deg, #16a34a, #15803d);
          border: none;
        }

        .back-card h3 {
          color: rgba(255,255,255,0.9);
          border-color: rgba(255,255,255,0.2);
        }

        .back-text {
          color: rgba(255,255,255,0.8);
          font-size: 14px;
          margin: 0 0 16px;
        }

        .back-link {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          padding: 12px 20px;
          background: rgba(255,255,255,0.2);
          border-radius: 10px;
          color: #fff;
          text-decoration: none;
          font-weight: 600;
          font-size: 14px;
          transition: background 0.2s;
        }

        .back-link:hover {
          background: rgba(255,255,255,0.3);
        }

        .map-loading {
          height: 300px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: var(--bg, #f8fafc);
          color: var(--muted, #6b7280);
          font-size: 14px;
        }

        @media (max-width: 600px) {
          .bikepark-stats-bar {
            flex-direction: column;
            gap: 12px;
          }

          .stat-item {
            min-width: auto;
            width: 100%;
          }

          .stat-favorite {
            margin-left: 0;
          }

          .location-actions {
            flex-direction: column;
          }

          .infra-grid {
            grid-template-columns: repeat(2, 1fr);
          }
        }
      `}</style>
    </div>
  );
}
